import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { backdropClose } from "../../../components/shared/backdrop";

interface TagEntry {
  tag: string;
  count: number;
}

export const Route = createFileRoute("/_authed/settings/tags")({
  component: TagsPage,
});

const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  boxSizing: "border-box",
};

const TAG_REGEX = /^[a-zA-Z0-9_-]+$/;

function TagsPage() {
  const qc = useQueryClient();
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<TagEntry | null>(null);

  const { data: tags = [], isLoading } = useQuery<TagEntry[]>({
    queryKey: ["agent-tags-global"],
    queryFn: () => api.get("/agents/tags"),
  });

  // "Create" a tag by assigning it to no agent — tags only exist via agent_tags.
  // So "creating" here is not applicable without an agent. We skip standalone create.
  // Instead we show an info note. Rename and delete are the main ops.

  const renameMut = useMutation({
    mutationFn: ({ old, newName }: { old: string; newName: string }) =>
      api.patch(`/agents/tags/${encodeURIComponent(old)}`, { new_name: newName }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tags-global"] }); setEditingTag(null); },
  });

  const deleteMut = useMutation({
    mutationFn: (tag: string) => api.delete(`/agents/tags/${encodeURIComponent(tag)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tags-global"] }); setDeleteTarget(null); },
  });

  function startEdit(tag: string) {
    setEditingTag(tag);
    setEditValue(tag);
  }

  function commitRename() {
    const v = editValue.trim();
    if (!v || v === editingTag) { setEditingTag(null); return; }
    if (!TAG_REGEX.test(v)) { return; }
    renameMut.mutate({ old: editingTag!, newName: v });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 640 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Tags globais</h1>
        <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>
          {tags.length} tag{tags.length !== 1 ? "s" : ""} em uso
        </span>
      </div>

      <div style={{ fontSize: 12, color: "var(--ink-dim)", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 14px" }}>
        Tags são criadas ao adicioná-las a um agente. Aqui você pode renomear ou excluir tags existentes — a alteração afeta todos os agentes que usam a tag.
      </div>

      {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}

      {tags.length === 0 && !isLoading && (
        <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhuma tag criada ainda.</span>
      )}

      {tags.length > 0 && (
        <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line)" }}>
                {["Tag", "Agentes", ""].map(h => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tags.map(entry => (
                <tr key={entry.tag} style={{ borderTop: "1px solid var(--line)" }}>
                  <td style={{ padding: "10px 16px" }}>
                    {editingTag === entry.tag ? (
                      <input
                        autoFocus
                        value={editValue}
                        onChange={e => setEditValue(e.target.value.replace(/\s/g, ""))}
                        onBlur={commitRename}
                        onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditingTag(null); }}
                        style={{ ...inputStyle, width: "auto", padding: "4px 8px", fontSize: 13 }}
                      />
                    ) : (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 10px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)" }}>
                        {entry.tag}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 16px", color: "var(--ink-muted)", fontSize: 13 }}>
                    {entry.count} agente{entry.count !== 1 ? "s" : ""}
                  </td>
                  <td style={{ padding: "10px 16px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                      <button
                        onClick={() => startEdit(entry.tag)}
                        style={{ fontSize: 12, color: "var(--ink-secondary)", background: "none", border: "none", cursor: "pointer" }}
                      >
                        Renomear
                      </button>
                      <button
                        onClick={() => setDeleteTarget(entry)}
                        style={{ fontSize: 12, color: "var(--danger)", background: "none", border: "none", cursor: "pointer" }}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div {...backdropClose(() => { setDeleteTarget(null); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setDeleteTarget(null)} role="alertdialog" label="Excluir tag" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 400, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Excluir tag</span>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
              Tem certeza que deseja excluir a tag{" "}
              <strong style={{ color: "var(--brand)" }}>{deleteTarget.tag}</strong>?
              Ela será removida de <strong>{deleteTarget.count}</strong> agente{deleteTarget.count !== 1 ? "s" : ""}.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{ padding: "7px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={() => deleteMut.mutate(deleteTarget.tag)}
                disabled={deleteMut.isPending}
                style={{ padding: "7px 16px", background: "var(--danger)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: deleteMut.isPending ? 0.6 : 1 }}
              >
                {deleteMut.isPending ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}
    </div>
  );
}
